// check-plan-monitor-telemetry-health.ts
//
// plan-monitor là VỎ (manifest.settings.hooks={}, xem tools/plan-monitor/tool.json
// trong pack) — enable/disable chỉ lật flag avatar-tools.json. Ruột thật (collector
// planning-council-monitor-collector.py) sống ở manifest tool "telemetry" và TỰ
// đọc flag plan-monitor để câm/nói.
//
// GAP E22: generic manifestHooksPresent() (check-enabled-tools-health.ts) iterate
// manifest.settings.hooks của CHÍNH tool — vỏ rỗng → loop rỗng → return true
// trivially → doctor báo "ok" dù collector câm hoàn toàn. Module này thay bằng
// check RIÊNG bám vào telemetry (nơi collector thật sự sống):
//   1. avatar-tools.json: tool "telemetry" có enabled=true không? Không → warn
//      (collector sẽ không bao giờ chạy dù plan-monitor bật — cần bật telemetry).
//   2. telemetry đã bật → derive DANH SÁCH EVENT ĐỘNG từ chính manifest telemetry
//      (KHÔNG hardcode — pack đăng ký collector ở event nào thì check đúng event
//      đó; hiện tại là PreToolUse+PostToolUse+SessionEnd nhưng số lượng có thể đổi
//      theo version pack, source-of-truth luôn là manifest, hết drift 2 nơi) rồi
//      verify settings.json THỰC SỰ có command collector ở TẤT CẢ event đó → event
//      nào thiếu → fail + fixable (re-apply TOÀN BỘ hookset telemetry, vá hook bị
//      ghi đè).
//
// KHÔNG phải special-case picker (đó là ai-orchestrator setup picker, TD10) — đây
// là exception hợp lý cho 1 tool vỏ-hybrid.

import {
  enableTool,
  readToolManifest,
  type ToolManifest,
} from "./apply-tool-manifest-to-settings.js";
import type { SettingsShape, ToolCheck } from "./check-enabled-tools-health.js";
import { commandsInEntries } from "./hook-command-list-from-entries.js";
import { readToolState } from "./tool-state-store.js";

const TELEMETRY_TOOL_NAME = "telemetry";
const COLLECTOR_COMMAND_HINT = "planning-council-monitor-collector.py";

// Command collector plan-monitor khai trong manifest telemetry ở 1 event, lọc
// theo hint filename (telemetry có nhiều hook khác — analytics/session/prompt —
// không liên quan plan-monitor).
function collectorCommandsByEvent(manifest: ToolManifest, event: string): string[] {
  return commandsInEntries(manifest.settings.hooks?.[event]).filter((cmd) =>
    cmd.includes(COLLECTOR_COMMAND_HINT),
  );
}

function settingsHasCommand(settings: SettingsShape, event: string, command: string): boolean {
  return commandsInEntries(settings.hooks?.[event]).includes(command);
}

// Derive DANH SÁCH EVENT collector đăng ký từ CHÍNH manifest telemetry — source
// of truth. Pack đăng ký ở 2 hay 3 (hay N) event đều đúng, không cần sửa code CLI
// khi pack đổi số event (đã từng lệch: code cũ hardcode 2 event Pre+Post, thực tế
// pack đăng ký thêm SessionEnd → check cũ mù nhánh backstop đó).
function collectorEventsFromManifest(manifest: ToolManifest): string[] {
  const hooks = manifest.settings.hooks ?? {};
  return Object.keys(hooks).filter((event) => collectorCommandsByEvent(manifest, event).length > 0);
}

export async function checkPlanMonitorTelemetryHealth(
  cwd: string,
  settings: SettingsShape,
): Promise<ToolCheck> {
  const name = "Tool: plan-monitor";
  const state = await readToolState(cwd);
  const telemetryEnabled = state.tools[TELEMETRY_TOOL_NAME]?.enabled === true;

  // (b) flag consistency: plan-monitor bật nhưng telemetry (ruột thật) tắt.
  if (!telemetryEnabled) {
    return {
      name,
      status: "warn",
      detail:
        "plan-monitor bật nhưng tool 'telemetry' (nơi collector thật sự sống) chưa enable " +
        "— sẽ không có dữ liệu dashboard. Chạy 'avatar tools enable telemetry'.",
      fixable: false,
    };
  }

  const telemetryManifest = await readToolManifest(cwd, TELEMETRY_TOOL_NAME);
  if (!telemetryManifest) {
    return {
      name,
      status: "warn",
      detail: "telemetry enabled nhưng pack thiếu manifest (version skew). Chạy 'avatar sync'.",
      fixable: false,
    };
  }

  // (a) collector presence: verify command thật có trong settings.json — ở TẤT CẢ
  // event manifest telemetry thật sự đăng ký (derived động, không hardcode).
  const collectorEvents = collectorEventsFromManifest(telemetryManifest);

  if (collectorEvents.length === 0) {
    // Manifest telemetry không còn khai collector ở đâu (rename/removed hint) —
    // version skew.
    return {
      name,
      status: "warn",
      detail:
        "không tìm thấy collector plan-monitor trong manifest telemetry hiện tại " +
        "(version skew?). Chạy 'avatar sync'.",
      fixable: false,
    };
  }

  const missingEvents: string[] = [];
  for (const event of collectorEvents) {
    const commands = collectorCommandsByEvent(telemetryManifest, event);
    const hasMissing = commands.some((cmd) => !settingsHasCommand(settings, event, cmd));
    if (hasMissing) {
      missingEvents.push(event);
    }
  }

  if (missingEvents.length > 0) {
    return {
      name,
      status: "fail",
      detail:
        `collector plan-monitor thiếu trong settings.json (${missingEvents.join(", ")}) ` +
        "— state nói telemetry bật nhưng hook bị ghi đè/mất. Fixable: sẽ re-apply " +
        "TOÀN BỘ hookset telemetry (không chỉ riêng plan-monitor — invariant " +
        "'telemetry enabled ⇒ full hookset') để khôi phục.",
      fixable: true,
      fix: async () => {
        await enableTool(cwd, telemetryManifest);
      },
    };
  }

  return {
    name,
    status: "ok",
    detail: `enabled, collector telemetry active (v${telemetryManifest.version})`,
    fixable: false,
  };
}
